import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageMeta from '../../components/common/PageMeta';
import {
  Callout,
  Field,
  Input,
  PublicActions,
  PublicLink,
  PublicPage,
  PublicPrimary,
  PublicSecondary,
  PublicShell,
  PublicTitle,
} from '../../ui/v2';
import { requestPasswordReset } from './authApi';
import { SIGN_IN_ERROR, emailError } from './authModel';

/** Seconds before "Send again" works, so a mail server can't be hammered. */
const RESEND_AFTER = 30;

/**
 * Forgot password — `/forgot-password`.
 *
 * Two screens. The request screen has one field and one primary. The sent
 * screen never confirms the account exists ("If an account exists for …")
 * and offers one retry path — "Send again" after a short wait — plus "Use a
 * different email" for a typo. A network failure is the only error shown.
 */
export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | undefined>();
  const [netError, setNetError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [cooldown, setCooldown] = useState(0);
  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = window.setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => window.clearTimeout(t);
  }, [cooldown]);

  const send = async (to: string) => {
    setBusy(true);
    setNetError(null);
    try {
      await requestPasswordReset(to);
      setSentTo(to);
      setCooldown(RESEND_AFTER);
    } catch {
      setNetError(SIGN_IN_ERROR.network);
    } finally {
      setBusy(false);
    }
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;
    const err = emailError(email);
    setError(err);
    if (err) {
      ref.current?.focus();
      return;
    }
    void send(email.trim());
  };

  return (
    <PublicPage>
      <PageMeta
        title="Reset your password · AskArthur"
        description="Reset your AskArthur password"
      />
      <PublicShell width="form">
        {sentTo ? (
          <>
            <PublicTitle
              lede={
                <>
                  If an account exists for{' '}
                  <span className="font-medium text-v2-fg">{sentTo}</span>, a
                  link to set a new password is on its way. It can take a minute
                  — check spam too.
                </>
              }
            >
              Check your email
            </PublicTitle>
            {netError && (
              <Callout tone="danger" className="mt-5">
                {netError}
              </Callout>
            )}
            <PublicActions align="between" className="mt-6">
              <PublicSecondary
                onClick={() => void send(sentTo)}
                loading={busy}
                disabledReason={
                  cooldown > 0
                    ? `You can send again in ${cooldown}s`
                    : undefined
                }
              >
                {cooldown > 0 ? (
                  <>
                    Send again in <span className="v2-num">{cooldown}s</span>
                  </>
                ) : (
                  'Send again'
                )}
              </PublicSecondary>
              <PublicPrimary onClick={() => navigate('/signin')}>
                Back to sign in
              </PublicPrimary>
            </PublicActions>
            <p className="mt-4 text-v2-12 text-v2-fg-3">
              Wrong address?{' '}
              <button
                type="button"
                className="rounded-v2-sm text-v2-fg-2 underline-offset-2 hover:underline"
                onClick={() => {
                  setSentTo(null);
                  setNetError(null);
                }}
              >
                Use a different email
              </button>
              .
            </p>
          </>
        ) : (
          <>
            <PublicTitle lede="Enter the email your account uses and we’ll send a link to set a new password.">
              Reset your password
            </PublicTitle>
            <form
              onSubmit={submit}
              noValidate
              className="mt-6 flex flex-col gap-4"
            >
              <Field label="Email" error={error}>
                {(id, describedBy) => (
                  <Input
                    ref={ref}
                    id={id}
                    type="email"
                    inputMode="email"
                    autoComplete="username"
                    autoFocus
                    aria-describedby={describedBy}
                    invalid={!!error}
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      if (error) setError(undefined);
                    }}
                    className="h-10 text-v2-14"
                  />
                )}
              </Field>
              {netError && <Callout tone="danger">{netError}</Callout>}
              <PublicPrimary
                type="submit"
                loading={busy}
                className="mt-1 sm:w-full"
              >
                Send reset link
              </PublicPrimary>
            </form>
            <p className="mt-6 text-v2-12">
              <PublicLink to="/signin" className="text-v2-12">
                ← Back to sign in
              </PublicLink>
            </p>
          </>
        )}
      </PublicShell>
    </PublicPage>
  );
}
